import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { trackError } from 'utils/trackError';
import { getAudioBridge, type AudioBridge } from 'utils/audio/audioBridge';
import { classifyMicError } from 'utils/audio/classifyMicError';
import { defaultAudioSettings } from 'utils/audio/defaultAudioSettings';
import { evaluateAudioDevices } from 'utils/audio/evaluateAudioDevices';
import {
  defaultOutputGroupId,
  filterAudioInputs,
  filterAudioOutputs,
} from 'utils/audio/filterAudioInputs';
import { matchesRecommendedDevice } from 'utils/audio/recommendedDevice';
import { selectSpeaker } from 'utils/audio/selectSpeaker';
import { stopStream } from 'utils/audio/stopStream';
import type {
  AudioDeviceState,
  AudioInputDevice,
  AudioOutputDevice,
  AudioSettings,
  MicAccessStatus,
  ProbeResult,
} from 'utils/audio/audioDeviceTypes';

export type AudioDeviceMode = 'active' | 'passive';
export type AudioEnvironmentKind = 'local' | 'remote' | 'web';

/** A null id leaves that device as it is. */
export type DeviceChoice = {
  microphoneId: string | null;
  speakerId: string | null;
  notifyWhenRecommendedAvailable: boolean;
};

export interface AudioDeviceContextValue {
  /** False until the mode is known. */
  ready: boolean;
  mode: AudioDeviceMode;
  environment: AudioEnvironmentKind;
  state: AudioDeviceState | 'passive';
  selectedDevice: AudioInputDevice | null;
  /** The passive mode needs no device, the active mode a chosen one. */
  canRecord: boolean;
  recommendedCandidate: AudioInputDevice | null;
  recommendedAvailable: boolean;
  devices: AudioInputDevice[];
  outputs: AudioOutputDevice[];
  selectedSpeaker: AudioOutputDevice | null;
  settings: AudioSettings;
  messageDismissed: boolean;
  reevaluate: () => Promise<void>;
  saveSettings: (next: AudioSettings) => Promise<void>;
  /** A Jabra clears the stored choice, any other device becomes it. */
  chooseDevices: (choice: DeviceChoice) => Promise<void>;
  switchToRecommendedDevice: () => Promise<void>;
  dismissMessage: () => void;
  setRecordingActive: (active: boolean) => void;
  openPrivacySettings: () => Promise<void>;
}

type Snapshot = {
  state: AudioDeviceState;
  selectedDevice: AudioInputDevice | null;
  recommendedCandidate: AudioInputDevice | null;
  recommendedAvailable: boolean;
  devices: AudioInputDevice[];
  outputs: AudioOutputDevice[];
  selectedSpeaker: AudioOutputDevice | null;
};

const INITIAL_SNAPSHOT: Snapshot = {
  state: 'searching',
  selectedDevice: null,
  recommendedCandidate: null,
  recommendedAvailable: false,
  devices: [],
  outputs: [],
  selectedSpeaker: null,
};

const chosenDevice = <T extends AudioInputDevice>(
  listed: T[],
  deviceId: string
): { label: string; deviceId: string; confirmedAt: string } | null => {
  const device = listed.find((candidate) => candidate.deviceId === deviceId);
  if (!device) throw new Error('The chosen audio device is no longer listed');
  return matchesRecommendedDevice(device.label)
    ? null
    : { label: device.label, deviceId: device.deviceId, confirmedAt: new Date().toISOString() };
};

// The floating progress popup is a second window of the same app; it must not touch the microphone.
const PROGRESS_POPUP_ROUTE = '/progress-indication-popup';

const AudioDeviceContext = createContext<AudioDeviceContextValue | undefined>(undefined);

async function probeMicrophone(constraints: MediaStreamConstraints): Promise<ProbeResult> {
  try {
    const stream = await navigator.mediaDevices.getUserMedia(constraints);
    stopStream(stream);
    return { ok: true };
  } catch (error) {
    return { ok: false, error: classifyMicError(error) };
  }
}

const labelsHidden = (devices: MediaDeviceInfo[]) => {
  const inputs = devices.filter((device) => device.kind === 'audioinput');
  return inputs.length > 0 && inputs.every((device) => device.label === '');
};

async function determineEnvironment(bridge: AudioBridge | null): Promise<AudioEnvironmentKind> {
  if (!bridge) return 'web';
  const environment = await bridge.getEnvironment();
  return environment.isRemoteSession ? 'remote' : 'local';
}

function modeFor(environment: AudioEnvironmentKind): AudioDeviceMode {
  if (environment !== 'local') return 'passive';
  if (typeof navigator.mediaDevices?.enumerateDevices !== 'function') return 'passive';
  if (window.location.pathname.startsWith(PROGRESS_POPUP_ROUTE)) return 'passive';
  return 'active';
}

export const AudioDeviceProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const bridgeRef = useRef<AudioBridge | null>(getAudioBridge());
  const [mode, setMode] = useState<AudioDeviceMode | null>(null);
  const [environment, setEnvironment] = useState<AudioEnvironmentKind>(
    bridgeRef.current ? 'local' : 'web'
  );
  const [snapshot, setSnapshot] = useState<Snapshot>(INITIAL_SNAPSHOT);
  const [settings, setSettingsState] = useState<AudioSettings>(defaultAudioSettings);
  const settingsRef = useRef<AudioSettings>(settings);
  const [messageDismissed, setMessageDismissed] = useState(false);
  const lastStateRef = useRef<AudioDeviceState>('searching');
  const recordingActiveRef = useRef(false);
  const runCounterRef = useRef(0);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const persistSettings = useCallback(async (next: AudioSettings) => {
    // Visible to evaluations right away, so a device change during the write sees the new settings.
    const previous = settingsRef.current;
    settingsRef.current = next;
    const written = await bridgeRef.current?.setSettings(next);
    if (written === false) {
      // A later write may already have moved on; only undo our own step.
      if (settingsRef.current === next) settingsRef.current = previous;
      throw new Error('Audio settings could not be written');
    }
    if (mountedRef.current) setSettingsState(next);
  }, []);

  const rememberRecommendedDevice = useCallback(
    async (candidate: AudioInputDevice | null) => {
      if (!candidate) return;
      const stored = settingsRef.current.recommendedMicrophone;
      if (stored?.label === candidate.label && stored.deviceId === candidate.deviceId) return;
      await persistSettings({
        ...settingsRef.current,
        recommendedMicrophone: {
          ...candidate,
          firstSeenAt: stored?.firstSeenAt ?? new Date().toISOString(),
        },
      });
    },
    [persistSettings]
  );

  const evaluateActive = useCallback(async () => {
    const runId = ++runCounterRef.current;
    const isCurrent = () => mountedRef.current && runId === runCounterRef.current;
    const skipProbe = recordingActiveRef.current;

    try {
      let all = await navigator.mediaDevices.enumerateDevices();
      let probe: ProbeResult | null = null;
      let permissionProbeSucceeded = false;
      if (labelsHidden(all) && !skipProbe) {
        const permissionProbe = await probeMicrophone({ audio: true });
        if (permissionProbe.ok) {
          permissionProbeSucceeded = true;
          all = await navigator.mediaDevices.enumerateDevices();
        } else {
          probe = permissionProbe;
        }
      }
      const devices = filterAudioInputs(all);
      const currentSettings = settingsRef.current;

      if (probe === null && !skipProbe) {
        const candidate = evaluateAudioDevices({
          devices,
          settings: currentSettings,
          probe: null,
          micAccessStatus: 'granted',
        }).selectedDevice;
        if (candidate) {
          probe = await probeMicrophone({ audio: { deviceId: { exact: candidate.deviceId } } });
        }
      }

      // Only the system can tell whether the microphone is blocked; a successful probe proves it is not.
      let micAccessStatus: MicAccessStatus = 'granted';
      const probeSucceeded = permissionProbeSucceeded || probe?.ok;
      if (!probeSucceeded && !skipProbe) {
        micAccessStatus = (await bridgeRef.current?.getMicAccessStatus()) ?? 'unknown';
      }
      if (!isCurrent()) return;

      const result = evaluateAudioDevices({
        devices,
        settings: currentSettings,
        probe,
        micAccessStatus,
      });
      const outputs = filterAudioOutputs(all);
      const selectedSpeaker = selectSpeaker({
        outputs,
        defaultGroupId: defaultOutputGroupId(all),
        settings: currentSettings,
      });
      setSnapshot({ ...result, devices, outputs, selectedSpeaker });
      if (result.state !== lastStateRef.current) {
        lastStateRef.current = result.state;
        setMessageDismissed(false);
      }
      await rememberRecommendedDevice(result.recommendedCandidate);
    } catch (error) {
      trackError(error, { context: 'audioDevice', action: 'evaluate' });
      // Without a first verdict every screen would wait forever; "no device" at least offers the retest.
      if (isCurrent() && lastStateRef.current === 'searching') {
        lastStateRef.current = 'noDevice';
        setSnapshot({ ...INITIAL_SNAPSHOT, state: 'noDevice' });
      }
    }
  }, [rememberRecommendedDevice]);

  useEffect(() => {
    let canceled = false;
    void (async () => {
      const bridge = bridgeRef.current;
      let resolvedMode: AudioDeviceMode;
      try {
        const resolvedEnvironment = await determineEnvironment(bridge);
        if (canceled) return;
        setEnvironment(resolvedEnvironment);
        resolvedMode = modeFor(resolvedEnvironment);
        if (resolvedMode === 'active' && bridge) {
          const stored = await bridge.getSettings();
          if (canceled) return;
          settingsRef.current = stored ?? defaultAudioSettings();
          setSettingsState(settingsRef.current);
        }
      } catch (error) {
        // Without a verdict the workstation falls back to the passive mode and the system default.
        trackError(error, { context: 'audioDevice', action: 'determineMode' });
        resolvedMode = 'passive';
      }
      if (canceled) return;
      setMode(resolvedMode);
      if (resolvedMode === 'active') await evaluateActive();
    })();
    return () => {
      canceled = true;
    };
  }, [evaluateActive]);

  useEffect(() => {
    if (mode !== 'active') return;
    const onDeviceChange = () => void evaluateActive();
    navigator.mediaDevices.addEventListener('devicechange', onDeviceChange);
    return () => navigator.mediaDevices.removeEventListener('devicechange', onDeviceChange);
  }, [mode, evaluateActive]);

  const reevaluate = useCallback(async () => {
    if (mode === 'active') await evaluateActive();
  }, [mode, evaluateActive]);

  const saveSettings = useCallback(
    async (next: AudioSettings) => {
      await persistSettings(next);
      await evaluateActive();
    },
    [persistSettings, evaluateActive]
  );

  const switchToRecommendedDevice = useCallback(async () => {
    await saveSettings({ ...settingsRef.current, confirmedMicrophone: null });
  }, [saveSettings]);

  const chooseDevices = useCallback(
    async ({ microphoneId, speakerId, notifyWhenRecommendedAvailable }: DeviceChoice) => {
      const current = settingsRef.current;
      await saveSettings({
        ...current,
        confirmedMicrophone:
          microphoneId === null
            ? current.confirmedMicrophone
            : chosenDevice(snapshot.devices, microphoneId),
        confirmedSpeaker:
          speakerId === null ? current.confirmedSpeaker : chosenDevice(snapshot.outputs, speakerId),
        notifyWhenRecommendedAvailable,
      });
    },
    [saveSettings, snapshot.devices, snapshot.outputs]
  );

  const dismissMessage = useCallback(() => setMessageDismissed(true), []);

  const setRecordingActive = useCallback((active: boolean) => {
    recordingActiveRef.current = active;
  }, []);

  const openPrivacySettings = useCallback(async () => {
    const opened = await bridgeRef.current?.openPrivacySettings();
    if (!opened) throw new Error('The microphone privacy settings could not be opened');
  }, []);

  const value = useMemo<AudioDeviceContextValue>(() => {
    // Unresolved counts as passive so that the web app never flashes the workstation entries.
    const resolvedMode = mode ?? 'passive';
    const hasSelectedDevice = mode === 'active' && snapshot.selectedDevice !== null;
    const resolvedState = resolvedMode === 'passive' ? 'passive' : snapshot.state;
    return {
      ready: mode !== null,
      mode: resolvedMode,
      environment,
      state: mode === null ? 'searching' : resolvedState,
      selectedDevice: mode === 'active' ? snapshot.selectedDevice : null,
      canRecord: mode !== null && (mode === 'passive' || hasSelectedDevice),
      recommendedCandidate: snapshot.recommendedCandidate,
      recommendedAvailable: snapshot.recommendedAvailable,
      devices: snapshot.devices,
      outputs: snapshot.outputs,
      selectedSpeaker: mode === 'active' ? snapshot.selectedSpeaker : null,
      settings,
      messageDismissed,
      reevaluate,
      saveSettings,
      chooseDevices,
      switchToRecommendedDevice,
      dismissMessage,
      setRecordingActive,
      openPrivacySettings,
    };
  }, [
    mode,
    environment,
    snapshot,
    settings,
    messageDismissed,
    reevaluate,
    saveSettings,
    chooseDevices,
    switchToRecommendedDevice,
    dismissMessage,
    setRecordingActive,
    openPrivacySettings,
  ]);

  return <AudioDeviceContext.Provider value={value}>{children}</AudioDeviceContext.Provider>;
};

export function useAudioDevice(): AudioDeviceContextValue {
  const context = useContext(AudioDeviceContext);
  if (!context) {
    throw new Error('useAudioDevice must be used within an AudioDeviceProvider');
  }
  return context;
}
